<template>
  <span class="checkbox-container" :class="{ [`${color}-color`]: true, [`size-${size}`]: true }" @click="onChecked(value)">
    <input v-model="value" type="checkbox" />
    <span class="checkmark" :class="{ disabled, indeterminate: value && indeterminate }"></span>
    <span v-if="title" class="checkmark-title">{{ title }}</span>
  </span>
</template>

<script>
export default {
  name: 'CustomCheckbox',
  props: {
    value: Boolean,
    color: String,
    disabled: {
      type: Boolean,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'm',
      // m - default, also supported: s
    },
    title: {
      type: String,
    },
  },
  methods: {
    onChecked(value) {
      if (!this.disabled) {
        this.$emit('input', !value);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

/* The container */
.checkbox-container {
  display: flex;
  position: relative;
  padding-left: rem(35);
  margin-bottom: $space-3;
  cursor: pointer;
  font-size: rem(22);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  &:hover input ~ .checkmark {
    &.disabled {
      background-color: transparent;
    }
  }

  span.checkmark {
    &.disabled {
      border: var(--border-secondary);
    }
  }
}

/* Hide the browser's default checkbox */
.checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: rem(20);
  width: rem(20);
  border: solid 2px;
  border-color: var(--border-secondary);
}

/* On mouse-over, add a grey background color */
.checkbox-container:hover input ~ .checkmark {
  background-color: var(--bg-tertiary);
}

/* When the checkbox is checked, add a blue background */
.checkbox-container input:checked ~ .checkmark {
  border: solid 2px;
  border-color: var(--text-primary);
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: '';
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.checkbox-container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.checkbox-container .checkmark:after {
  left: rem(6);
  top: rem(2);
  width: rem(5);
  height: rem(10);
  border: solid var(--text-primary);
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

.checkbox-container .checkmark.indeterminate:after {
  left: rem(7);
  top: rem(3);
  width: 0;
  height: rem(10);
  border: solid var(--text-primary);
  border-width: 0 0 2px 0;
  -webkit-transform: rotate(90deg);
  -ms-transform: rotate(90deg);
  transform: rotate(90deg);
}

/* Custom colors input by parameter */
.checkbox-container.red-color {
  input:checked ~ .checkmark {
    border-color: var(--status-error);
  }

  .checkmark:after {
    border: solid var(--status-error);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.green-color {
  input:checked ~ .checkmark {
    border-color: var(--status-success);
  }

  .checkmark:after {
    border: solid var(--status-success);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.blue-color {
  input:checked ~ .checkmark {
    border-color: var(--brand-secondary);
  }

  .checkmark:after {
    border: solid var(--brand-secondary);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.light-green-color {
  input:checked ~ .checkmark {
    border-color: var(--status-success);
  }

  .checkmark:after {
    border: solid var(--status-success);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.cold-marine-color {
  input:checked ~ .checkmark {
    border-color: var(--brand-primary);
  }

  .checkmark:after {
    border: solid var(--brand-primary);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.pink-color {
  input:checked ~ .checkmark {
    border-color: var(--status-error);
  }

  .checkmark:after {
    border: solid var(--status-error);
    border-width: 0 2px 2px 0;
  }
}

.checkbox-container.size-s {
  .checkmark {
    height: rem(18);
    width: rem(18);
  }

  .checkmark:after {
    left: rem(5);
    top: rem(1);
    height: rem(9);
  }

  .checkmark.indeterminate:after {
    left: rem(6);
    top: rem(3);
    height: rem(8);
  }
}

.checkmark-title {
  font-family: $font-family-regular;
  font-size: $font-size-base;
  margin-left: rem(-8);
}
</style>
